import React, { useState, useRef, useMemo, useEffect, useCallback } from 'react'
import { withTranslation, useTranslation } from 'react-i18next'
import { useQuery, useMutation, gql } from '@apollo/client'
import { getRestaurantProfile, editRestaurant, getCuisines, updateOrderModes, sendRestaurantTempPassword, toggleSelfDelivery, setSelfDeliverySchedule, updateAutoAccept, setRestaurantDispatchMode, setRestaurantFallbackPolicy } from '../apollo'
import ConfigurableValues from '../config/constants'
import { searchPlaces } from '../utils/mapbox'
import defaultLogo from '../assets/img/defaultLogo.png'
import {
  Box, Alert, Typography, Button, Checkbox,
  Select, OutlinedInput, MenuItem, ListItemText, TextField,
  Paper, List, ListItemButton, FormControlLabel, Switch,
  CircularProgress, Divider, IconButton, InputAdornment
} from '@mui/material'
import { ThemeProvider } from '@mui/material/styles'
import CustomLoader from '../components/Loader/CustomLoader'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import SendIcon from '@mui/icons-material/Send'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded'
import { SHOP_TYPE } from '../utils/enums'
import Dropdown from '../components/Dropdown'
import TimezoneSelect from '../components/TimezoneSelect'
import SelfDeliveryPanel from '../components/SelfDeliverySchedule/SelfDeliveryPanel'
import { profileTheme, PF } from './profileTheme'

const GET_PROFILE = gql`${getRestaurantProfile}`
const EDIT_RESTAURANT = gql`${editRestaurant}`
const CUISINES = gql`${getCuisines}`
const UPDATE_ORDER_MODES = gql`${updateOrderModes}`
const SEND_TEMP_PASSWORD = gql`${sendRestaurantTempPassword}`
const TOGGLE_SELF_DELIVERY = gql`${toggleSelfDelivery}`
const SET_SELF_DELIVERY_SCHEDULE = gql`${setSelfDeliverySchedule}`
const UPDATE_AUTO_ACCEPT = gql`${updateAutoAccept}`
const SET_DISPATCH_MODE = gql`${setRestaurantDispatchMode}`
const SET_FALLBACK_POLICY = gql`${setRestaurantFallbackPolicy}`

const ITEM_HEIGHT = 48
const ITEM_PADDING_TOP = 8
const MenuProps = {
  PaperProps: { style: { maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP, width: 250 } }
}

const HELPER = { fontSize: 14, color: PF.secondary, lineHeight: 1.45 }

// Field grids respond to the width of the card's field column (a CSS
// container), not the window, so they also reflow when the sidebar collapses.
// Each step is [min container width in px, columns].
const fieldGrid = (...steps) => ({
  display: 'grid',
  columnGap: 3,
  rowGap: 2.5,
  gridTemplateColumns: 'minmax(0, 1fr)',
  ...Object.fromEntries(steps.map(([min, cols]) => [
    `@container (min-width: ${min}px)`,
    { gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }
  ]))
})
const GRID_2 = fieldGrid([400, 2])
const GRID_3 = fieldGrid([660, 3])
const GRID_4 = fieldGrid([340, 2], [680, 4])
const TOGGLE_GRID = { ...fieldGrid([340, 2], [760, 4]), columnGap: 1.5, rowGap: 1.5 }
const IMAGE_GRID = {
  display: 'grid',
  gap: 3,
  gridTemplateColumns: 'minmax(0, 1fr)',
  '@container (min-width: 520px)': { gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)' }
}
const DIVIDER = { my: 3, borderColor: PF.border }
const FOOTER = { mt: 3, pt: 2.5, borderTop: `1px solid ${PF.border}` }
const FOOTER_ACTIONS = { display: 'flex', justifyContent: 'flex-end' }
const PREVIEW = { height: 220, boxSizing: 'border-box', borderRadius: '10px', overflow: 'hidden', mb: 1.5, border: `1px solid ${PF.offBorder}`, bgcolor: PF.page }
const CUISINE_CHIP = {
  display: 'inline-flex', alignItems: 'center', px: '10px', py: '4px', borderRadius: '6px',
  bgcolor: PF.indigoSoft, color: PF.indigoText, fontSize: 14, fontWeight: 600, lineHeight: 1.3
}

// A switch row: label on the left, switch on the right, the whole row is the
// FormControlLabel's <label>, so a click anywhere on it toggles the switch.
const toggleRow = on => ({
  m: 0,
  minHeight: 52,
  px: '14px',
  py: 0,
  gap: '10px',
  boxSizing: 'border-box',
  justifyContent: 'space-between',
  borderRadius: '10px',
  border: '1px solid',
  borderColor: on ? PF.onBorder : PF.offBorder,
  bgcolor: on ? PF.onBg : PF.card,
  cursor: 'pointer',
  transition: 'background-color 150ms ease, border-color 150ms ease',
  '&:hover': { borderColor: on ? '#7FC29A' : PF.inputBorder },
  '& .MuiFormControlLabel-label': { flex: 1, minWidth: 0 },
  '@media (prefers-reduced-motion: reduce)': { transition: 'none' }
})

// Dispatch Mode's two switches. `other` is the mode a switch hands over to when
// it is turned off, so one of the two is always on.
const DISPATCH_MODES = [
  {
    mode: 'AUTO',
    other: 'MANUAL',
    title: 'Auto-assign to closest staff driver',
    text: 'New orders are automatically assigned to your nearest available staff driver.',
  },
  {
    mode: 'MANUAL',
    other: 'AUTO',
    title: 'Manual: I will pick the driver',
    text: 'New orders wait in your dispatch panel for you to assign manually.',
  },
]

const FIELDS_COLUMN = { minWidth: 0, containerType: 'inline-size' }

// With a title: title + description on the left, fields on the right. Without
// one (Restaurant Information, whose heading is the page header), the fields
// take the card's full width.
const SectionCard = ({ title, subtitle, children }) => (
  <Paper elevation={0} component="section" sx={{
    bgcolor: PF.card, border: `1px solid ${PF.border}`, borderRadius: '14px', boxShadow: 'none',
    p: { xs: 2.5, sm: 4 }, containerType: 'inline-size'
  }}>
    {title ? (
      <Box sx={{
        display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', rowGap: 3,
        '@container (min-width: 760px)': { gridTemplateColumns: '200px minmax(0, 1fr)', columnGap: 6 }
      }}>
        <Box>
          <Typography component="h2" sx={{ fontSize: 18, fontWeight: 700, lineHeight: 1.35, color: PF.text }}>{title}</Typography>
          <Typography sx={{ ...HELPER, mt: 0.75 }}>{subtitle}</Typography>
        </Box>
        <Box sx={FIELDS_COLUMN}>
          {children}
        </Box>
      </Box>
    ) : (
      <Box sx={FIELDS_COLUMN}>
        {children}
      </Box>
    )}
  </Paper>
)

const FieldLabel = ({ children }) => (
  <Typography sx={sx.label}>{children}</Typography>
)

const sx = {
  input: { width: '100%' },
  label: { fontSize: 13, fontWeight: 600, color: PF.label, lineHeight: 1.4, mb: '6px' },
  btn: { minWidth: 140 },
  toggleLabel: { fontSize: 14, fontWeight: 500, color: PF.text, lineHeight: 1.3 },
  rowTitle: { fontSize: 15, fontWeight: 600, color: PF.text, lineHeight: 1.35 },
  rowText: { ...HELPER, mt: 0.25 },
  stepBtn: { minWidth: 44, width: 44, px: 0, fontSize: 18 },
  saved: { display: 'flex', alignItems: 'center', gap: 0.75 },
  savedText: { fontSize: 14, color: PF.primary, fontWeight: 600 }
}

const VendorProfile = () => {
  const { SERVER_URL, MAPBOX_TOKEN } = ConfigurableValues()
  const { t } = useTranslation()
  const restaurantId = localStorage.getItem('restaurantId')

  // Info form fields
  const [name, setName] = useState('')
  const [username, setUsername] = useState('')
  const [deliveryTime, setDeliveryTime] = useState('')
  const [minimumOrder, setMinimumOrder] = useState('')
  const [salesTax, setSalesTax] = useState('')
  const [orderPrefix, setOrderPrefix] = useState('')
  const [shopType, setShopType] = useState(SHOP_TYPE.RESTAURANT)
  const [restaurantCuisines, setRestaurantCuisines] = useState([])
  const [enableDelivery, setEnableDelivery] = useState(true)
  const [enablePickup, setEnablePickup] = useState(true)
  const [enablePickupCOD, setEnablePickupCOD] = useState(false)
  const [selfDelivery, setSelfDelivery] = useState(false)
  // The Self Delivery switch was just turned on: its schedule isn't saved yet.
  const [selfDeliveryDraft, setSelfDeliveryDraft] = useState(false)
  // Food truck: a restaurant that parks somewhere different every day.
  const [isMobile, setIsMobile] = useState(false)
  const [mobileDeliveryEnabled, setMobileDeliveryEnabled] = useState(false)
  const [mobileDeliveryRadius, setMobileDeliveryRadius] = useState('')
  const [timezone, setTimezone] = useState('')
  const [autoAccept, setAutoAccept] = useState(false)
  const [defaultPrepTime, setDefaultPrepTime] = useState(15)
  const [autoAcceptSuccess, setAutoAcceptSuccess] = useState('')
  const [dispatchMode, setDispatchMode] = useState('AUTO')
  const [dispatchModeSuccess, setDispatchModeSuccess] = useState('')
  const [fallbackPolicy, setFallbackPolicy] = useState('IF_STAFF_BUSY')
  const [fallbackPolicySuccess, setFallbackPolicySuccess] = useState('')
  const [phone, setPhone] = useState('')

  // Address autocomplete
  const [addressValue, setAddressValue] = useState('')
  const [addressCoords, setAddressCoords] = useState(null)
  const [predictions, setPredictions] = useState([])
  const [showPredictions, setShowPredictions] = useState(false)
  const searchTimerRef = useRef(null)

  // Images
  const [imgUrl, setImgUrl] = useState('')       // preview URL (base64 or existing URL)
  const [logoUrl, setLogoUrl] = useState('')
  const [imgFile, setImgFile] = useState(null)   // actual File object for upload
  const [logoFile, setLogoFile] = useState(null)
  const imgOriginal = useRef('')
  const logoOriginal = useRef('')

  // Alerts
  const [infoError, setInfoError] = useState('')
  const [infoSuccess, setInfoSuccess] = useState('')

  // Password fields
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showCurrent, setShowCurrent] = useState(false)
  const [showNew, setShowNew] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [pwError, setPwError] = useState('')
  const [pwSuccess, setPwSuccess] = useState('')

  // Track which mutation is in flight: 'info' | 'password' | null
  const [savingInfo, setSavingInfo] = useState(false)
  const [savingPw, setSavingPw] = useState(false)
  const [savingImages, setSavingImages] = useState(false)
  const [imageSuccess, setImageSuccess] = useState('')
  const [imageError, setImageError] = useState('')

  const [sendingTempPw, setSendingTempPw] = useState(false)
  const [tempPwMsg, setTempPwMsg] = useState('')
  const [tempPwError, setTempPwError] = useState('')

  const { data, error: errorQuery, loading: loadingQuery } = useQuery(GET_PROFILE, { variables: { id: restaurantId } })
  const { data: cuisinesData } = useQuery(CUISINES)
  const [mutate] = useMutation(EDIT_RESTAURANT, { refetchQueries: [GET_PROFILE] })
  const [mutateOrderModes] = useMutation(UPDATE_ORDER_MODES, { refetchQueries: [GET_PROFILE] })
  const [mutateSendTempPassword] = useMutation(SEND_TEMP_PASSWORD)
  const [mutateToggleSelfDelivery] = useMutation(TOGGLE_SELF_DELIVERY, { refetchQueries: [GET_PROFILE] })
  const [mutateSelfDeliverySchedule] = useMutation(SET_SELF_DELIVERY_SCHEDULE, { refetchQueries: [GET_PROFILE] })
  const [mutateAutoAccept, { loading: savingAutoAccept }] = useMutation(UPDATE_AUTO_ACCEPT, {
    refetchQueries: [GET_PROFILE],
    onCompleted: () => { setAutoAcceptSuccess('Saved!'); setTimeout(() => setAutoAcceptSuccess(''), 3000) },
    onError: (err) => console.error('Auto accept error:', err)
  })
  const [mutateDispatchMode, { loading: savingDispatchMode }] = useMutation(SET_DISPATCH_MODE, {
    refetchQueries: [GET_PROFILE],
    onCompleted: () => { setDispatchModeSuccess('Saved!'); setTimeout(() => setDispatchModeSuccess(''), 3000) },
    onError: (err) => console.error('Dispatch mode error:', err)
  })
  const [mutateFallbackPolicy, { loading: savingFallbackPolicy }] = useMutation(SET_FALLBACK_POLICY, {
    refetchQueries: [GET_PROFILE],
    onCompleted: () => { setFallbackPolicySuccess('Saved!'); setTimeout(() => setFallbackPolicySuccess(''), 3000) },
    onError: (err) => console.error('Fallback policy error:', err)
  })

  // Self delivery all day, every day (the old switch): platform Delivery must be
  // off. A part-day schedule leaves the rest of the day to RushMyFood.
  const savedSelfMode = data?.restaurant?.selfDeliveryMode
  const selfDeliveryAllDay = selfDelivery && (!savedSelfMode || savedSelfMode === 'ALWAYS') && !data?.restaurant?.selfDeliveryStartsAt

  const cuisinesInDropdown = useMemo(() => cuisinesData?.cuisines?.map(c => c.name) ?? [], [cuisinesData])

  // Populate form from query data — runs on every refetch so selfDelivery stays in sync
  useEffect(() => {
    if (!data?.restaurant) return
    const r = data.restaurant
    setName(r.name || '')
    setUsername(r.username || '')
    setAddressValue(r.address || '')
    setDeliveryTime(r.deliveryTime != null ? String(r.deliveryTime) : '')
    setMinimumOrder(r.minimumOrder != null ? String(r.minimumOrder) : '')
    setSalesTax(r.tax != null ? String(r.tax) : '')
    setOrderPrefix(r.orderPrefix || '')
    setShopType(r.shopType || SHOP_TYPE.RESTAURANT)
    setRestaurantCuisines(r.cuisines ?? [])
    setPhone(r.phone || '')
    setEnableDelivery(r.enableDelivery !== false)
    setEnablePickup(r.enablePickup !== false)
    setEnablePickupCOD(r.enablePickupCOD === true)
    setSelfDelivery(r.selfDelivery === true)
    setIsMobile(r.isMobile === true)
    setMobileDeliveryEnabled(r.mobileDeliveryEnabled === true)
    setMobileDeliveryRadius(r.mobileDeliveryRadiusMiles != null ? String(r.mobileDeliveryRadiusMiles) : '')
    setTimezone(r.timezone || '')
    setAutoAccept(r.autoAccept === true)
    setDefaultPrepTime(r.defaultPrepTime || 15)
    setDispatchMode(r.dispatchMode || 'AUTO')
    setFallbackPolicy(r.fallbackToPlatform || 'IF_STAFF_BUSY')
    if (r.image) { setImgUrl(r.image); imgOriginal.current = r.image }
    if (r.logo) { setLogoUrl(r.logo); logoOriginal.current = r.logo }
  }, [data?.restaurant]) // re-run whenever server data changes (catches refetch after toggleSelfDelivery)

  const handleAddressChange = useCallback((value) => {
    setAddressValue(value)
    setAddressCoords(null)
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
    if (value.length > 2) {
      searchTimerRef.current = setTimeout(async () => {
        try {
          const results = await searchPlaces(value, MAPBOX_TOKEN, { limit: 5 })
          setPredictions(results)
          setShowPredictions(results.length > 0)
        } catch (e) {
          setPredictions([])
          setShowPredictions(false)
        }
      }, 300)
    } else {
      setPredictions([])
      setShowPredictions(false)
    }
  }, [MAPBOX_TOKEN])

  const handlePredictionSelect = useCallback((prediction) => {
    setAddressValue(prediction.placeName)
    setPredictions([])
    setShowPredictions(false)
    if (prediction.lat != null && prediction.lng != null) {
      setAddressCoords({ latitude: prediction.lat, longitude: prediction.lng })
    }
  }, [])

  const handleImagePick = (event, type) => {
    const file = event.target.files?.[0]
    if (!file) return
    const preview = URL.createObjectURL(file)
    if (type === 'image') { setImgUrl(preview); setImgFile(file) }
    else { setLogoUrl(preview); setLogoFile(file) }
  }

  const uploadFileToBackend = async(file) => {
    if (!file) return null
    const formData = new FormData()
    formData.append('image', file)
    const base = SERVER_URL.endsWith('/') ? SERVER_URL : `${SERVER_URL}/`
    const res = await fetch(`${base}upload/image`, { method: 'POST', body: formData })
    const json = await res.json()
    if (!res.ok) throw new Error(json.error || 'Upload failed')
    return json.url
  }

  const handleSaveImages = async() => {
    setSavingImages(true)
    setImageError('')
    setImageSuccess('')
    try {
      const uploadedImg = imgFile ? await uploadFileToBackend(imgFile) : imgOriginal.current
      const uploadedLogo = logoFile ? await uploadFileToBackend(logoFile) : logoOriginal.current
      await mutate({
        variables: {
          restaurantInput: {
            _id: restaurantId,
            name: data?.restaurant?.name || '',
            address: data?.restaurant?.address || '',
            username: data?.restaurant?.username || '',
            password: data?.restaurant?.password || '',
            image: uploadedImg || data?.restaurant?.image || '',
            logo: uploadedLogo || data?.restaurant?.logo || defaultLogo,
            orderPrefix: data?.restaurant?.orderPrefix || '',
            deliveryTime: data?.restaurant?.deliveryTime || 0,
            minimumOrder: data?.restaurant?.minimumOrder || 0,
            salesTax: data?.restaurant?.tax || 0,
            phone: data?.restaurant?.phone || '',
            shopType: data?.restaurant?.shopType || 'RESTAURANT',
          }
        }
      })
      if (uploadedImg) { imgOriginal.current = uploadedImg; setImgUrl(uploadedImg); setImgFile(null) }
      if (uploadedLogo) { logoOriginal.current = uploadedLogo; setLogoUrl(uploadedLogo); setLogoFile(null) }
      setImageSuccess('Images saved successfully!')
      setTimeout(() => setImageSuccess(''), 4000)
    } catch (err) {
      setImageError(err.message || err.graphQLErrors?.[0]?.message || 'Failed to save images')
      setTimeout(() => setImageError(''), 4000)
    } finally {
      setSavingImages(false)
    }
  }

  const handleSaveInfo = async(e) => {
    e.preventDefault()
    if (!name.trim() || !addressValue.trim()) {
      setInfoError('Name and address are required')
      setTimeout(() => setInfoError(''), 4000)
      return
    }
    if (enableDelivery && selfDeliveryAllDay) {
      setInfoError('Delivery and Self Delivery cannot both be enabled. Turn one off before saving.')
      setTimeout(() => setInfoError(''), 5000)
      return
    }
    setSavingInfo(true)
    try {
      const uploadedImg = imgFile ? await uploadFileToBackend(imgFile) : imgOriginal.current
      const uploadedLogo = logoFile ? await uploadFileToBackend(logoFile) : logoOriginal.current
      const restaurantInput = {
        _id: restaurantId,
        name: name.trim(),
        address: addressValue.trim(),
        username: username.trim(),
        password: data?.restaurant?.password || '',
        image: uploadedImg || data?.restaurant?.image || '',
        logo: uploadedLogo || data?.restaurant?.logo || defaultLogo,
        orderPrefix: orderPrefix.trim(),
        deliveryTime: Number(deliveryTime) || 0,
        minimumOrder: Number(minimumOrder) || 0,
        salesTax: Number(salesTax) || 0,
        phone: phone.trim(),
        shopType,
        cuisines: restaurantCuisines
        // firstDeliveryMerchantId is intentionally NOT sent here — it is an
        // internal integration field managed by super admins on the Restaurant
        // Readiness page. Omitting it leaves the stored value untouched (the
        // editRestaurant resolver only updates fields that are present).
      }
      if (addressCoords) {
        restaurantInput.location = { latitude: addressCoords.latitude, longitude: addressCoords.longitude }
      }
      // '' = Automatic: the backend works the zone out from the address.
      restaurantInput.timezone = timezone.trim() || null
      // Food truck settings. The radius only means anything when the truck
      // actually delivers, so it is sent alongside the toggle.
      restaurantInput.isMobile = isMobile
      if (isMobile) {
        restaurantInput.mobileDeliveryEnabled = mobileDeliveryEnabled
        restaurantInput.mobileDeliveryRadiusMiles = mobileDeliveryEnabled && mobileDeliveryRadius !== ''
          ? Number(mobileDeliveryRadius)
          : null
      }
      await mutate({ variables: { restaurantInput } })
      if (uploadedImg) { imgOriginal.current = uploadedImg; setImgFile(null) }
      if (uploadedLogo) { logoOriginal.current = uploadedLogo; setLogoFile(null) }
      setInfoSuccess('Restaurant updated successfully')
      setTimeout(() => setInfoSuccess(''), 5000)
    } catch (err) {
      setInfoError(err.graphQLErrors?.[0]?.message || 'Failed to save changes')
      setTimeout(() => setInfoError(''), 5000)
    } finally {
      setSavingInfo(false)
    }
  }

  const handleChangePassword = async(e) => {
    e.preventDefault()
    setPwError('')
    setPwSuccess('')
    if (!currentPassword) { setPwError('Enter your current password'); return }
    if (newPassword.length < 6) { setPwError('New password must be at least 6 characters'); return }
    if (newPassword !== confirmPassword) { setPwError('Passwords do not match'); return }

    setSavingPw(true)
    try {
      const r = data.restaurant
      const restaurantInput = {
        _id: restaurantId,
        name: r.name,
        address: r.address,
        username: r.username,
        password: newPassword,
        image: r.image || '',
        logo: r.logo || defaultLogo,
        orderPrefix: r.orderPrefix,
        deliveryTime: r.deliveryTime,
        minimumOrder: r.minimumOrder,
        salesTax: r.tax,
        shopType: r.shopType,
        cuisines: r.cuisines
      }
      await mutate({ variables: { restaurantInput } })
      setPwSuccess('Password updated successfully')
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setTimeout(() => setPwSuccess(''), 5000)
    } catch (err) {
      setPwError(err.graphQLErrors?.[0]?.message || 'Failed to update password')
    } finally {
      setSavingPw(false)
    }
  }

  const handleSendTempPassword = async() => {
    setSendingTempPw(true)
    setTempPwMsg('')
    setTempPwError('')
    try {
      await mutateSendTempPassword({ variables: { restaurantId } })
      setTempPwMsg('Temporary password sent to vendor email!')
      setTimeout(() => setTempPwMsg(''), 6000)
    } catch(err) {
      setTempPwError(err.graphQLErrors?.[0]?.message || 'Failed to send email')
      setTimeout(() => setTempPwError(''), 5000)
    } finally {
      setSendingTempPw(false)
    }
  }

  return (
    <ThemeProvider theme={profileTheme}>
      <Box sx={{
        bgcolor: PF.page, color: PF.text, fontFamily: PF.font, fontSize: 15, lineHeight: 1.45,
        minHeight: { xs: 'calc(100vh - 80px)', sm: '100vh' }
      }}>
        <Box sx={{
          maxWidth: 1100, mx: 'auto', boxSizing: 'content-box',
          px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 4.5 }, pb: { xs: 5, md: 7 }
        }}>
          {/* Page header: Restaurant Information's heading, above its card */}
          <Box component="header" sx={{ mb: 3 }}>
            <Typography component="h1" sx={{ fontSize: { xs: 26, sm: 30 }, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.2, color: PF.text }}>
              Restaurant Information
            </Typography>
            <Typography sx={{ ...HELPER, mt: 0.75 }}>
              Update your restaurant details
            </Typography>
          </Box>

          {errorQuery && <Alert severity="error" sx={{ mb: 3 }}>{errorQuery.message}</Alert>}
          {loadingQuery ? <CustomLoader /> : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Restaurant Information (its heading is the page header above) */}
              <SectionCard>
                <form onSubmit={handleSaveInfo}>
                  <Box sx={GRID_2}>
                    <Box>
                      <FieldLabel>Restaurant Name</FieldLabel>
                      <TextField fullWidth size="small" placeholder="Restaurant name" value={name} onChange={e => setName(e.target.value)} sx={sx.input} />
                    </Box>
                    <Box>
                      <FieldLabel>Username (for app login)</FieldLabel>
                      <TextField fullWidth size="small" placeholder="Username" value={username} onChange={e => setUsername(e.target.value.replace(/ /g, ''))} sx={sx.input} />
                    </Box>
                    <Box>
                      <FieldLabel>Address</FieldLabel>
                      <Box sx={{ position: 'relative' }}>
                        <TextField
                          fullWidth size="small" placeholder="Restaurant address"
                          value={addressValue}
                          onChange={e => handleAddressChange(e.target.value)}
                          onBlur={() => setTimeout(() => setShowPredictions(false), 200)}
                          onFocus={() => predictions.length > 0 && setShowPredictions(true)}
                          sx={sx.input}
                        />
                        {showPredictions && predictions.length > 0 && (
                          <Paper sx={{ position: 'absolute', zIndex: 1000, width: '100%', maxHeight: 240, overflow: 'auto', mt: 0.5, borderRadius: '10px', border: `1px solid ${PF.border}`, boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)' }}>
                            <List dense disablePadding>
                              {predictions.map(p => (
                                <ListItemButton key={p.id} onMouseDown={() => handlePredictionSelect(p)} sx={{ py: 1, minHeight: 44, borderBottom: `1px solid ${PF.border}` }}>
                                  <ListItemText
                                    primary={p.name || p.placeName}
                                    secondary={p.placeName || ''}
                                    primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }}
                                    secondaryTypographyProps={{ fontSize: 13 }}
                                  />
                                </ListItemButton>
                              ))}
                            </List>
                          </Paper>
                        )}
                      </Box>
                    </Box>
                    <Box>
                      <FieldLabel>Phone Number</FieldLabel>
                      <TextField fullWidth size="small" placeholder="+1 555 000 0000" value={phone} onChange={e => setPhone(e.target.value)} sx={sx.input} />
                    </Box>
                    {/* Every order time, opening hour and schedule for this
                        restaurant is read on this clock. Filled in from the
                        address; "Automatic" works it out again on save. */}
                    <Box>
                      <FieldLabel>Time Zone</FieldLabel>
                      <TimezoneSelect
                        value={timezone}
                        onChange={setTimezone}
                        notSetLabel="Automatic (from the address)"
                        sx={sx.input}
                      />
                      <Typography sx={{ fontSize: 13, color: 'text.secondary', lineHeight: 1.45, mt: 0.75 }}>
                        Order times, opening hours and schedules all use this time zone.
                      </Typography>
                    </Box>
                  </Box>

                  <Divider sx={DIVIDER} />

                  <Box sx={GRID_4}>
                    <Box>
                      <FieldLabel>Delivery Time (min)</FieldLabel>
                      <TextField fullWidth size="small" placeholder="30" type="number" value={deliveryTime} onChange={e => setDeliveryTime(e.target.value)} sx={sx.input} />
                    </Box>
                    <Box>
                      <FieldLabel>Minimum Order ($)</FieldLabel>
                      <TextField fullWidth size="small" placeholder="0" type="number" value={minimumOrder} onChange={e => setMinimumOrder(e.target.value)} sx={sx.input} />
                    </Box>
                    <Box>
                      <FieldLabel>Sales Tax (%)</FieldLabel>
                      <TextField fullWidth size="small" placeholder="0" type="number" value={salesTax} onChange={e => setSalesTax(e.target.value)} sx={sx.input} />
                    </Box>
                    <Box>
                      <FieldLabel>Order Prefix</FieldLabel>
                      <TextField fullWidth size="small" placeholder="Order prefix" value={orderPrefix} onChange={e => setOrderPrefix(e.target.value)} sx={sx.input} />
                    </Box>
                  </Box>

                  <Divider sx={DIVIDER} />

                  <Box sx={GRID_2}>
                    <Box>
                      <FieldLabel>Shop Category</FieldLabel>
                      <Select
                        fullWidth size="small"
                        value={shopType}
                        onChange={e => setShopType(e.target.value)}
                        input={<OutlinedInput />}
                        IconComponent={KeyboardArrowDownRoundedIcon}
                      >
                        {Object.values(SHOP_TYPE).map(v => (
                          <MenuItem key={v} value={v}>{v}</MenuItem>
                        ))}
                      </Select>
                    </Box>
                    <Box>
                      <FieldLabel>Cuisines</FieldLabel>
                      <Select
                        multiple fullWidth size="small"
                        value={restaurantCuisines}
                        onChange={e => {
                          const val = e.target.value
                          setRestaurantCuisines(typeof val === 'string' ? val.split(',') : val)
                        }}
                        input={<OutlinedInput />}
                        IconComponent={KeyboardArrowDownRoundedIcon}
                        renderValue={selected => (
                          <Box component="span" sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px', minWidth: 0 }}>
                            {selected.map(cuisine => (
                              <Box component="span" key={cuisine} sx={CUISINE_CHIP}>{cuisine}</Box>
                            ))}
                          </Box>
                        )}
                        MenuProps={MenuProps}
                        sx={{ '& .MuiSelect-select': { py: '7px' } }}
                      >
                        {cuisinesInDropdown.map(cuisine => (
                          <MenuItem key={cuisine} value={cuisine}>
                            <Checkbox checked={restaurantCuisines.indexOf(cuisine) > -1} size="small" />
                            <ListItemText primary={cuisine} />
                          </MenuItem>
                        ))}
                      </Select>
                    </Box>
                  </Box>

                  <Divider sx={DIVIDER} />

                  <Box>
                    <FieldLabel>Order Modes</FieldLabel>
                    <Box sx={TOGGLE_GRID}>
                      <FormControlLabel
                        labelPlacement="start"
                        sx={toggleRow(enableDelivery)}
                        control={<Switch checked={enableDelivery} onChange={async e => {
                          const enabled = e.target.checked
                          setEnableDelivery(enabled)
                          // Delivery and all-day Self Delivery are mutually exclusive;
                          // a part-day schedule needs RushMyFood for the rest.
                          if (enabled && selfDeliveryAllDay) {
                            setSelfDelivery(false)
                            await mutateToggleSelfDelivery({ variables: { restaurantId, enabled: false } })
                          }
                          await mutateOrderModes({ variables: { restaurantId, enableDelivery: enabled, enablePickup, enablePickupCOD } })
                        }} color="primary" size="small" />}
                        label={<Typography sx={sx.toggleLabel}>Delivery</Typography>}
                      />
                      <FormControlLabel
                        labelPlacement="start"
                        sx={toggleRow(enablePickup)}
                        control={<Switch checked={enablePickup} onChange={e => { setEnablePickup(e.target.checked); mutateOrderModes({ variables: { restaurantId, enableDelivery, enablePickup: e.target.checked, enablePickupCOD } }) }} color="primary" size="small" />}
                        label={<Typography sx={sx.toggleLabel}>Pickup</Typography>}
                      />
                      <FormControlLabel
                        labelPlacement="start"
                        sx={toggleRow(enablePickupCOD)}
                        control={<Switch checked={enablePickupCOD} onChange={e => { setEnablePickupCOD(e.target.checked); mutateOrderModes({ variables: { restaurantId, enableDelivery, enablePickup, enablePickupCOD: e.target.checked } }) }} color="primary" size="small" />}
                        label={<Typography sx={sx.toggleLabel}>COD for Pickup</Typography>}
                      />
                      <FormControlLabel
                        labelPlacement="start"
                        sx={toggleRow(selfDelivery || selfDeliveryDraft)}
                        control={<Switch checked={selfDelivery || selfDeliveryDraft} onChange={async e => {
                          // On: pick when (the panel below saves it). Off: back to
                          // RushMyFood drivers at once, with platform Delivery on.
                          if (e.target.checked) {
                            setSelfDeliveryDraft(true)
                            return
                          }
                          setSelfDeliveryDraft(false)
                          if (!selfDelivery) return
                          setSelfDelivery(false)
                          setEnableDelivery(true)
                          await mutateSelfDeliverySchedule({ variables: { restaurantId, mode: 'OFF', fromMinute: null } })
                        }} color="warning" size="small" />}
                        label={<Typography sx={sx.toggleLabel}>Self Delivery</Typography>}
                      />
                    </Box>
                    {(selfDelivery || selfDeliveryDraft) && (
                      <SelfDeliveryPanel
                        restaurantId={restaurantId}
                        restaurant={data?.restaurant}
                        draft={selfDeliveryDraft && !selfDelivery}
                        onCancel={() => setSelfDeliveryDraft(false)}
                        onSaved={() => setSelfDeliveryDraft(false)}
                      />
                    )}
                    {selfDelivery && (
                      <Box sx={{ mt: 1.5, px: '14px', py: 1.5, bgcolor: '#fff8f0', border: '1px solid #fed7aa', borderRadius: '10px' }}>
                        <Typography sx={{ fontSize: 14, color: '#c2410c', lineHeight: 1.45 }}>
                          Self Delivery is enabled. Make sure to set up your delivery zones in the <strong>Delivery Zones</strong> page.
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Food truck. A truck's address comes from its stops, so the
                      address field above is ignored while this is on. */}
                  <Box sx={{ mt: 3 }}>
                    <Typography sx={sx.label}>Food Truck</Typography>
                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                      <FormControlLabel
                        labelPlacement="start"
                        sx={toggleRow(isMobile)}
                        control={<Switch checked={isMobile} onChange={e => setIsMobile(e.target.checked)} color="success" size="small" />}
                        label={<Typography sx={sx.toggleLabel}>This is a food truck</Typography>}
                      />
                      {isMobile && (
                        <FormControlLabel
                          labelPlacement="start"
                          sx={toggleRow(mobileDeliveryEnabled)}
                          control={<Switch checked={mobileDeliveryEnabled} onChange={e => setMobileDeliveryEnabled(e.target.checked)} color="success" size="small" />}
                          label={<Typography sx={sx.toggleLabel}>Deliver from the truck</Typography>}
                        />
                      )}
                    </Box>

                    {isMobile && (
                      <Box sx={{ mt: 1.5, p: 2, bgcolor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px' }}>
                        <Typography sx={{ fontSize: 14, color: '#166534', lineHeight: 1.45, mb: 2 }}>
                          This truck&apos;s address comes from its stops, not the address field above.
                          Set where it parks on the <strong>Truck Stops</strong> page.
                        </Typography>

                        {mobileDeliveryEnabled && (
                          <Box sx={{ mb: 2 }}>
                            <Typography sx={sx.label}>Delivery radius (miles from wherever it is parked)</Typography>
                            <TextField
                              type="number"
                              size="small"
                              value={mobileDeliveryRadius}
                              onChange={e => setMobileDeliveryRadius(e.target.value)}
                              inputProps={{ min: 0.5, max: 10, step: 0.5 }}
                              sx={{ ...sx.input, maxWidth: 160 }}
                            />
                            <Typography sx={{ fontSize: 14, color: '#166534', lineHeight: 1.45, mt: 0.75 }}>
                              5 means customers within 5 miles of today&apos;s spot; the circle moves with the truck.
                            </Typography>
                          </Box>
                        )}

                        {data?.restaurant?.serviceLocation && (
                          <Box sx={{ pt: 1.5, borderTop: '1px solid #bbf7d0' }}>
                            <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#166534' }}>
                              {data.restaurant.serviceLocation.isOpenNow
                                ? `Parked now: ${data.restaurant.serviceLocation.label || data.restaurant.serviceLocation.address}`
                                : (data.restaurant.serviceLocation.nextStopLabel || 'No stop scheduled')}
                            </Typography>
                            {data.restaurant.serviceLocation.windowLabel && (
                              <Typography sx={{ fontSize: 14, color: '#166534' }}>
                                {data.restaurant.serviceLocation.windowLabel}
                                {data.restaurant.currentStop?.isCheckedIn ? ' · checked in on the POS' : ''}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>

                  <Box sx={FOOTER}>
                    {infoSuccess && <Alert severity="success" sx={{ mb: 2 }}>{infoSuccess}</Alert>}
                    {infoError && <Alert severity="error" sx={{ mb: 2 }}>{infoError}</Alert>}
                    <Box sx={FOOTER_ACTIONS}>
                      <Button type="submit" variant="contained" disabled={savingInfo} sx={sx.btn}>
                        {savingInfo ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Save Changes'}
                      </Button>
                    </Box>
                  </Box>
                </form>
              </SectionCard>

              {/* Auto Accept Orders */}
              <SectionCard
                title="Auto Accept Orders"
                subtitle="Automatically accept incoming orders without manual review"
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  <FormControlLabel
                    labelPlacement="start"
                    sx={{ ...toggleRow(autoAccept), py: 1.25 }}
                    control={
                      <Switch
                        checked={autoAccept}
                        onChange={e => setAutoAccept(e.target.checked)}
                        color="primary"
                        size="small"
                      />
                    }
                    label={
                      <Box>
                        <Typography sx={sx.rowTitle}>
                          Auto Accept Orders
                        </Typography>
                        <Typography sx={sx.rowText}>
                          {autoAccept ? 'Orders are automatically accepted' : 'Orders require manual acceptance'}
                        </Typography>
                      </Box>
                    }
                  />
                  {autoAccept && (
                    <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 2, rowGap: 1, flexWrap: 'wrap' }}>
                      <Typography sx={{ fontSize: 15, color: PF.text, fontWeight: 500, minWidth: 160 }}>
                        Default Prep Time
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Button
                          size="small" variant="outlined"
                          onClick={() => setDefaultPrepTime(p => Math.max(5, p - 5))}
                          sx={sx.stepBtn}
                        >−</Button>
                        <Typography sx={{ fontWeight: 700, fontSize: 15, minWidth: 64, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
                          {defaultPrepTime} min
                        </Typography>
                        <Button
                          size="small" variant="outlined"
                          onClick={() => setDefaultPrepTime(p => p + 5)}
                          sx={sx.stepBtn}
                        >+</Button>
                      </Box>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      disabled={savingAutoAccept}
                      onClick={() => mutateAutoAccept({ variables: { restaurantId, autoAccept, prepTime: defaultPrepTime } })}
                      sx={sx.btn}
                      startIcon={savingAutoAccept ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                      {savingAutoAccept ? 'Saving...' : 'Save'}
                    </Button>
                    {autoAcceptSuccess && (
                      <Box sx={sx.saved}>
                        <CheckCircleOutlineIcon sx={{ color: PF.primary, fontSize: 18 }} />
                        <Typography sx={sx.savedText}>{autoAcceptSuccess}</Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </SectionCard>

              {/* Dispatch Mode — shown only while self-delivery is on */}
              {selfDelivery && (
                <SectionCard
                  title="Dispatch Mode"
                  subtitle="How orders are routed to your staff drivers when you do self-delivery"
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    {/* One switch per mode. Exactly one is on: turning one on
                        turns the other off, and turning one off turns the other on. */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {DISPATCH_MODES.map(m => (
                        <FormControlLabel
                          key={m.mode}
                          labelPlacement="start"
                          sx={{ ...toggleRow(dispatchMode === m.mode), py: 1.25 }}
                          control={
                            <Switch
                              checked={dispatchMode === m.mode}
                              onChange={e => setDispatchMode(e.target.checked ? m.mode : m.other)}
                              color="primary"
                              size="small"
                            />
                          }
                          label={
                            <Box>
                              <Typography sx={sx.rowTitle}>{m.title}</Typography>
                              <Typography sx={sx.rowText}>{m.text}</Typography>
                            </Box>
                          }
                        />
                      ))}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                      <Button
                        variant="contained"
                        disabled={savingDispatchMode}
                        onClick={() => mutateDispatchMode({ variables: { restaurantId, mode: dispatchMode } })}
                        sx={sx.btn}
                        startIcon={savingDispatchMode ? <CircularProgress size={16} color="inherit" /> : null}
                      >
                        {savingDispatchMode ? 'Saving...' : 'Save'}
                      </Button>
                      {dispatchModeSuccess && (
                        <Box sx={sx.saved}>
                          <CheckCircleOutlineIcon sx={{ color: PF.primary, fontSize: 18 }} />
                          <Typography sx={sx.savedText}>{dispatchModeSuccess}</Typography>
                        </Box>
                      )}
                    </Box>

                    {/* Per-restaurant fallback policy — only relevant for self-delivery */}
                    {selfDelivery && (
                      <Box sx={{ mt: 0.5, pt: 3, borderTop: `1px solid ${PF.border}`, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box>
                          <Typography sx={{ fontSize: 16, fontWeight: 700, color: PF.text, mb: 0.5 }}>
                            Platform-rider backup
                          </Typography>
                          <Typography sx={HELPER}>
                            What should happen when your own staff drivers can&apos;t take an order? This overrides the platform-wide setting.
                          </Typography>
                        </Box>
                        <Select
                          size="small"
                          value={fallbackPolicy}
                          onChange={e => setFallbackPolicy(e.target.value)}
                          IconComponent={KeyboardArrowDownRoundedIcon}
                          sx={{ width: '100%', maxWidth: 460 }}
                        >
                          <MenuItem value="NEVER">Never — only my staff drivers deliver</MenuItem>
                          <MenuItem value="IF_STAFF_BUSY">If staff busy — fall back to platform riders (recommended)</MenuItem>
                          <MenuItem value="ALWAYS">Always — try platform riders first, my staff as backup</MenuItem>
                        </Select>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                          <Button
                            variant="contained"
                            disabled={savingFallbackPolicy}
                            onClick={() => mutateFallbackPolicy({ variables: { restaurantId, policy: fallbackPolicy } })}
                            sx={sx.btn}
                            startIcon={savingFallbackPolicy ? <CircularProgress size={16} color="inherit" /> : null}
                          >
                            {savingFallbackPolicy ? 'Saving...' : 'Save'}
                          </Button>
                          {fallbackPolicySuccess && (
                            <Box sx={sx.saved}>
                              <CheckCircleOutlineIcon sx={{ color: PF.primary, fontSize: 18 }} />
                              <Typography sx={sx.savedText}>{fallbackPolicySuccess}</Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    )}
                  </Box>
                </SectionCard>
              )}

              {/* App Credentials */}
              <SectionCard
                title="App Credentials"
                subtitle="Manage the restaurant app login password"
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', columnGap: 3, rowGap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ flex: '1 1 260px', minWidth: 0 }}>
                    <Typography sx={{ fontSize: 15, color: PF.text, mb: 0.5, overflowWrap: 'anywhere' }}>
                      Vendor Email: <strong>{data?.restaurant?.owner?.email || '—'}</strong>
                    </Typography>
                    <Typography sx={HELPER}>
                      A random temporary password will be generated and emailed to the vendor. They can use it to log into the restaurant app.
                    </Typography>
                  </Box>
                  <Box sx={{ flexShrink: 0, maxWidth: '100%' }}>
                    <Button
                      variant="outlined"
                      disabled={sendingTempPw}
                      onClick={handleSendTempPassword}
                      startIcon={sendingTempPw ? <CircularProgress size={16} /> : <SendIcon fontSize="small" />}
                      sx={{ maxWidth: '100%' }}
                    >
                      {sendingTempPw ? 'Sending...' : 'Send Temp Password to Email'}
                    </Button>
                    {tempPwMsg && (
                      <Box sx={{ ...sx.saved, mt: 1.5 }}>
                        <CheckCircleOutlineIcon sx={{ color: PF.primary, fontSize: 18 }} />
                        <Typography sx={sx.savedText}>{tempPwMsg}</Typography>
                      </Box>
                    )}
                    {tempPwError && <Alert severity="error" sx={{ mt: 1.5, py: 0 }}>{tempPwError}</Alert>}
                  </Box>
                </Box>
              </SectionCard>

              {/* Change Password */}
              <SectionCard
                title="Change Password"
                subtitle="Update your restaurant app login password"
              >
                <form onSubmit={handleChangePassword}>
                  <Box sx={GRID_3}>
                    {[
                      { label: 'Current Password', value: currentPassword, set: setCurrentPassword, show: showCurrent, toggle: () => setShowCurrent(v => !v), placeholder: 'Current password' },
                      { label: 'New Password', value: newPassword, set: setNewPassword, show: showNew, toggle: () => setShowNew(v => !v), placeholder: 'Min 6 characters' },
                      { label: 'Confirm New Password', value: confirmPassword, set: setConfirmPassword, show: showConfirm, toggle: () => setShowConfirm(v => !v), placeholder: 'Repeat new password' }
                    ].map(({ label, value, set, show, toggle, placeholder }) => (
                      <Box key={label}>
                        <FieldLabel>{label}</FieldLabel>
                        <TextField
                          fullWidth size="small"
                          placeholder={placeholder}
                          type={show ? 'text' : 'password'}
                          value={value}
                          onChange={e => set(e.target.value)}
                          sx={sx.input}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton size="small" onClick={toggle} edge="end">
                                  {show ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                </IconButton>
                              </InputAdornment>
                            )
                          }}
                        />
                      </Box>
                    ))}
                  </Box>
                  <Box sx={FOOTER}>
                    {pwSuccess && <Alert severity="success" sx={{ mb: 2 }}>{pwSuccess}</Alert>}
                    {pwError && <Alert severity="error" sx={{ mb: 2 }}>{pwError}</Alert>}
                    <Box sx={FOOTER_ACTIONS}>
                      <Button type="submit" variant="contained" disabled={savingPw} sx={sx.btn}>
                        {savingPw ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Update Password'}
                      </Button>
                    </Box>
                  </Box>
                </form>
              </SectionCard>

              {/* Images */}
              <SectionCard
                title="Images"
                subtitle="Update restaurant banner image and logo"
              >
                <Box sx={IMAGE_GRID}>
                  <Box>
                    <FieldLabel>Banner Image</FieldLabel>
                    <Box sx={PREVIEW}>
                      <img
                        alt="Banner"
                        src={imgUrl || 'https://enatega.com/wp-content/uploads/2023/11/man-suit-having-breakfast-kitchen-side-view.webp'}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                      />
                    </Box>
                    <Button variant="outlined" component="label" size="small">
                      Change Banner
                      <input type="file" hidden accept="image/*" onChange={e => handleImagePick(e, 'image')} />
                    </Button>
                  </Box>
                  <Box>
                    <FieldLabel>Logo</FieldLabel>
                    <Box sx={{ ...PREVIEW, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
                      <img alt="Logo" src={logoUrl || defaultLogo} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                    </Box>
                    <Button variant="outlined" component="label" size="small">
                      Change Logo
                      <input type="file" hidden accept="image/*" onChange={e => handleImagePick(e, 'logo')} />
                    </Button>
                  </Box>
                </Box>
                <Box sx={FOOTER}>
                  {imageError && <Typography color="error" variant="body2" sx={{ mb: 1 }}>{imageError}</Typography>}
                  {imageSuccess && <Typography color="success.main" variant="body2" sx={{ mb: 1 }}>{imageSuccess}</Typography>}
                  <Box sx={FOOTER_ACTIONS}>
                    <Button
                      variant="contained"
                      disabled={savingImages}
                      onClick={handleSaveImages}
                      sx={sx.btn}
                    >
                      {savingImages ? 'Saving...' : 'Save Images'}
                    </Button>
                  </Box>
                </Box>
              </SectionCard>
            </Box>
          )}
        </Box>
      </Box>
    </ThemeProvider>
  )
}

export default withTranslation()(VendorProfile)
